import React, { createContext, useState, useContext, useEffect } from 'react';
import * as authService from './auth.Service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSessionExpiring, setIsSessionExpiring] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('[AUTH_CONTEXT] Error parsing stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    } else {
      // Limpiar en caso de que solo uno de los dos exista
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const data = await authService.login(email, password);
      const userData = data.data;
      const token = data.token;

      // Clear any existing session data first
      localStorage.clear();
      
      // Set new session data
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', token);
      localStorage.setItem('loginTime', Date.now().toString());
      setUser(userData);
      
      return data;
    } catch (error) {
      console.error('[AUTH_CONTEXT] Error in login:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      throw error;
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('token');
    const currentUser = user;

    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);

    if (currentUser && token) {
      try {
        await authService.logout(currentUser.id, token);
      } catch (error) {
        console.warn('[AUTH_CONTEXT] Error logging out on server:', error.message);
      }
    }
  };

  const isAuthenticated = () => {
    return user !== null && localStorage.getItem('token') !== null;
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      setIsSessionExpiring(true);
      setTimeout(() => {
        setUser(null);
        setIsSessionExpiring(false);
      }, 1000);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  const value = { 
    user, 
    loading, 
    login, 
    logout, 
    isAuthenticated,
    isSessionExpiring,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};